<template>
    <div class="details-page" v-if="run">
        <div class="md-layout">
            <div class="md-layout-item">
                <div class="custom-toolbar">
                    <div class="custom-toolbar-start">
                        <h3 class="title" v-if="run.runName != null">Run {{ run.runName }}</h3>
                    </div>
                    <div class="custom-toolbar-end" v-if="!$root.isMobileOnly && !isReadOnlyUser">
                        <div class="header-button-container">
                            <run-status-button
                                class="status-btn"
                                class-name="md-sm"
                                :run="run"
                                @statusUpdated="handleChangedRunStatus"
                            />

                            <run-edit-button :run="run" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="md-layout md-alignment-top-left content-layout-panel">
            <div
                class="md-layout-item md-small-size-100 md-medium-size-100 md-layout md-large-size-66 md-xlarge-size-50"
            >
                <div class="md-layout-item md-large-size-100 md-medium-size-100 md-small-size-100 md-xlarge-size-100">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-blue">
                            <div class="card-icon">
                                <md-icon>description</md-icon>
                            </div>
                        </md-card-header>
                        <md-card-content>
                            <div class="single-row three-column-box">
                                <div class="custom-md-field details-box">
                                    <div class="custom-label">Assigned To</div>
                                    <router-link
                                        v-if="run.assignedTo && run.assignedTo.publicUserId"
                                        :to="{
                                            name: 'Team Member Profile',
                                            params: { publicUserId: run.assignedTo.publicUserId }
                                        }"
                                        target="_blank"
                                    >
                                        {{ run.assignedTo.fullName || run.assignedTo.publicUserId }}
                                    </router-link>
                                    <span v-else>Unassigned</span>
                                </div>
                                <div class="details-box">
                                    <label>Customer</label>
                                    <span class="value">{{ run.customerName }}</span>
                                </div>
                                <div class="details-box">
                                    <label>Rate Group</label>
                                    <span class="value">{{ run.rateGroupName }}</span>
                                </div>
                            </div>
                            <div class="single-row three-column-box">
                                <div class="details-box">
                                    <label>Run Date</label>
                                    <span class="value">{{ run.runDate | dateFormat(DATE_TYPES.standardDate) }}</span>
                                </div>
                                <div class="details-box">
                                    <label>Start Time</label>
                                    <span class="value">
                                        {{ run.startTime | dateTimeFormat(DATE_TYPES.standardDate) }}
                                    </span>
                                </div>
                                <div class="details-box">
                                    <label>Completed Time</label>
                                    <span class="value">
                                        {{ run.completedDate | dateTimeFormat(DATE_TYPES.standardDate) }}
                                    </span>
                                </div>
                            </div>
                        </md-card-content>
                    </md-card>
                </div>

                <div class="md-layout-item md-small-size-100 md-large-size-100 md-xlarge-size-100">
                    <md-card>
                        <md-card-header class="md-card-header-icon md-card-header-warning">
                            <div class="card-icon">
                                <md-icon>list</md-icon>
                            </div>
                        </md-card-header>
                        <md-card-content>
                            <p v-if="!run.stopList || !run.stopList.length" class="grayedout-text text-center">
                                No Stops
                            </p>
                            <stop-list-table v-else :trip-details="run" />
                        </md-card-content>
                    </md-card>
                </div>
            </div>

            <div
                class="md-layout-item md-small-size-100 md-layout md-medium-size-100 md-large-size-33 md-xlarge-size-50"
            >
                <div
                    v-if="!isIndividualUser"
                    class="md-layout-item md-small-size-100 md-large-size-100 md-xlarge-size-100"
                >
                    <charge-rates-list
                        v-if="canAccessRates || isAdmin || isReadOnlyUser"
                        :charge-lines="run.chargeLines"
                        :has-invoice="run.customerInvoiceId !== null"
                        title="Run Charges"
                        @updateRunDetails="getRunDetails"
                        :rates-have-changed="updateChargesInBackground"
                        :run-details="run"
                    />
                </div>
                <div
                    v-if="!isIndividualUser"
                    class="md-layout-item md-small-size-100 md-large-size-100 md-xlarge-size-100"
                >
                    <cost-rates-list
                        v-if="canAccessRates || isAdmin"
                        :cost-lines="run.costLines"
                        :has-invoice="run.teamMemberInvoiceId !== null"
                        title="Run Costs"
                        @updateRunDetails="getRunDetails"
                        :rates-have-changed="updateCostsInBackground"
                        :run-details="run"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { RUN_STATUS_CONSTANTS, DATE_TYPES, HUB_ACTIONS, HUB_NAMES, SIGNALR_CONNECTION_STATE } from '@/utils/constants';
import { ChargeRatesList, CostRatesList } from '@/components';
import { handleRequests, showErrorMessage } from '@/helpers';
import StopListTable from '@/pages/Trips/Components/StopListTable';
import RunStatusButton from './Buttons/RunStatusButton';
import RunEditButton from './Buttons/RunEditButton';

export default {
    name: 'TripRunsDetails',
    components: {
        RunStatusButton,
        RunEditButton,
        ChargeRatesList,
        CostRatesList,
        StopListTable
    },
    mixins: [GeneralMixin],
    data() {
        return {
            statuses: Object.entries(RUN_STATUS_CONSTANTS).map(([key, value]) => ({ key, value })),
            DATE_TYPES,
            isAdmin: Boolean(this.$route.query.isAdmin),
            run: {
                runId: null,
                runName: null,
                runNumber: null,
                runStatus: 'Pending',
                runDate: null,
                completedDate: null,
                startTime: null,
                customerName: null,
                customerId: null,
                rateGroupName: null,
                rateGroupId: null,
                assignedTo: {},
                stops: [],
                costLines: [],
                chargeLines: [],
                customerInvoiceId: null,
                teamMemberInvoiceId: null
            },
            signalRHandlers: [],
            signalRTimeout: null,
            updateCostsInBackground: false,
            updateChargesInBackground: false
        };
    },
    computed: {
        ...mapGetters({
            isReadOnlyUser: 'user/isReadOnlyUser',
            isTeamOwner: 'user/isTeamOwner',
            isIndividualUser: 'user/isIndividualUser',
            teamRegions: 'team/teamRegions',
            getConnectionStatus: 'signalR/getConnectionStatus',
            getConnection: 'signalR/getConnection',
            canAccessRates: 'user/canAccessRates'
        })
    },
    async mounted() {
        this.$_handleLoaderState(true);
        await this.getRunDetails();
        this.$_handleLoaderState(false);

        if (this.isTeamOwner || this.isAdmin) {
            // Set up SignalR handlers
            this.signalRHandlers = [
                {
                    action: 'RunRatesUpdated',
                    handler: async (runId, updateCosts, updateCharges) => {
                        this.handleRunRatesUpdated(runId, updateCosts, updateCharges);
                    }
                },
                {
                    action: 'ProcessRecalculationRunComplete',
                    handler: async (runId) => {
                        this.handleRunRatesUpdated(runId, true, true);
                    }
                }
            ];
            this.setupSignalR();
        }
    },
    methods: {
        async getRunDetails() {
            try {
                const api = `/api/runs/${this.$route.params.runId}/details`;
                const run = await handleRequests(api);

                this.run = run.data;
            } catch (e) {
                showErrorMessage(this, e.data, null);
            }
        },
        handleChangedRunStatus({ runId, runStatus }) {
            if (this.run.runId === runId) {
                this.$set(this.run, 'runStatus', runStatus);
            }
        },
        async setupSignalR() {
            try {
                await this.$store.dispatch('signalR/connect', HUB_NAMES.RatesEngineUpdateHub);
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                // Verify connection is established before proceeding
                if (!connection || connection.state !== SIGNALR_CONNECTION_STATE.Connected) {
                    throw new Error('SignalR connection not established');
                }

                await connection.invoke(HUB_ACTIONS.JoinToTeamChannel);
                await this.$store.dispatch('signalR/registerHandlers', {
                    hubName: HUB_NAMES.RatesEngineUpdateHub,
                    handlers: this.signalRHandlers
                });
            } catch (e) {
                console.error('Error connecting to rates engine update hub:', e);
            }
        },
        removeSignalRHandlers() {
            try {
                const connection = this.getConnection(HUB_NAMES.RatesEngineUpdateHub);

                if (connection && connection.state === SIGNALR_CONNECTION_STATE.Connected) {
                    this.$store.dispatch('signalR/unregisterHandlers', {
                        hubName: HUB_NAMES.RatesEngineUpdateHub,
                        handlers: this.signalRHandlers
                    });
                    connection.invoke(HUB_ACTIONS.RemoveFromTeamChannel);
                }
            } catch (error) {
                console.error('Error removing SignalR handlers:', error);
            }
        },
        async handleRunRatesUpdated(runId, updateCosts, updateCharges) {
            if (Number(runId) === this.run.runId) {
                // fetch the new rates
                try {
                    if (this.run.teamMemberInvoiceId === null || this.run.teamMemberInvoiceId === undefined) {
                        this.updateCostsInBackground = Boolean(updateCosts);
                    }

                    if (this.run.customerInvoiceId === null || this.run.customerInvoiceId === undefined) {
                        this.updateChargesInBackground = Boolean(updateCharges);
                    }

                    const endpoint = `/api/runs/get-rates/${runId}`;
                    const result = await handleRequests(endpoint);

                    if (this.updateCostsInBackground) {
                        this.run.costLines = result.data.costLines;
                    }

                    if (this.updateChargesInBackground) {
                        this.run.chargeLines = result.data.chargeLines;
                    }
                } finally {
                    this.updateCostsInBackground = false;
                    this.updateChargesInBackground = false;
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.content-layout-panel {
    > div {
        padding: 0;
    }
}

.details-box {
    display: inline-block;
    vertical-align: top;
    min-width: 150px;
}

.single-row > div:first-child {
    width: 20%;
}
.three-column-box > div:first-child {
    width: 20%;
}
.three-column-box > div:nth-child(2) {
    width: 20%;
}
.three-column-box > div:last-child {
    width: 40%;
}

.details-page {
    padding-top: 5px;
}

.header-button-container {
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;

    > button {
        margin-right: 0;
    }

    .status-btn {
        display: inline-block;
        text-transform: uppercase;
        font-size: 12px;
        > ::v-deep span {
            margin: 6px 8px;
            height: 36px;
            line-height: 36px;
            border-radius: 2px;
            margin-right: 0px;
        }

        > ::v-deep .dropdown-menu {
            margin-left: 9px;
            margin-top: 0;
        }
    }
}

::v-deep .md-field.md-theme-default::before,
::v-deep .md-field.md-theme-default::after {
    bottom: 0;
    display: none;
}
</style>
